import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Loader2,
  UserCheck,
  Package,
  Clock,
  Workflow,
} from 'lucide-react';
import { useAgentStudioNavigate } from '../lib/routes';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/States';
import { Modal } from '../components/ui/Modal';
import { RunStatusBadge } from '../components/StatusBadges';
import { ArtifactViewer } from '../features/agents/ArtifactViewer';
import { runsService } from '../services/runsService';
import { flowsService } from '../services/flowsService';
import { NODE_DEF_BY_KIND } from '../lib/catalog';
import { formatDuration } from '../lib/utils';
import type { Run, RunEvent, Artifact } from '../types';

export function RunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useAgentStudioNavigate();
  const [run, setRun] = useState<Run | null>(null);
  const [loading, setLoading] = useState(true);
  const [artifact, setArtifact] = useState<Artifact | null>(null);
  // Mapa nodeId → rótulo amigável (busca o fluxo da execução).
  const [nodeLabels, setNodeLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!id) return;
    runsService.get(id).then((r) => {
      setRun(r ?? null);
      setLoading(false);
      if (r?.flowId) {
        flowsService.get(r.flowId).then((f) => {
          if (!f) return;
          const map: Record<string, string> = {};
          f.nodes.forEach((n) => {
            map[n.id] = (n.data?.label as string) || NODE_DEF_BY_KIND[n.kind]?.label || n.kind;
          });
          setNodeLabels(map);
        }).catch(() => {});
      }
    });
  }, [id]);

  if (loading) {
    return (
      <div className="p-8">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-6 h-96 w-full" />
      </div>
    );
  }

  if (!run) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3">
        <p className="text-slate">Execução não encontrada.</p>
        <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/runs')}>Voltar</Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between gap-4 border-b border-line bg-white px-8 py-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" icon={ArrowLeft} onClick={() => navigate('/runs')} aria-label="Voltar" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-ink">{run.flowName}</h1>
              <RunStatusBadge status={run.status} />
            </div>
            <p className="flex items-center gap-1.5 text-xs text-slate">
              <Clock className="h-3.5 w-3.5" />
              {new Date(run.startedAt).toLocaleString('pt-BR')} · {formatDuration(run.durationMs)}
            </p>
          </div>
        </div>
        <Button variant="secondary" icon={Workflow} onClick={() => navigate(`/flows/${run.flowId}`)}>
          Abrir fluxo
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin p-8">
        <div className="mx-auto grid max-w-4xl grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Timeline */}
          <Card className="lg:col-span-2">
            <div className="border-b border-line px-5 py-4">
              <h2 className="text-base font-semibold text-ink">Passo a passo & logs</h2>
            </div>
            <div className="p-5">
              <ol className="relative space-y-4 border-l border-line pl-6">
                {run.events.map((e, i) => (
                  <TimelineItem key={i} event={e} label={nodeLabels[e.nodeId]} />
                ))}
              </ol>
            </div>
          </Card>

          {/* Entrada / saída / artefatos */}
          <div className="space-y-4">
            {run.input && (
              <Card>
                <div className="border-b border-line px-5 py-4">
                  <h2 className="text-base font-semibold text-ink">Entrada</h2>
                </div>
                <div className="p-4">
                  <pre className="max-h-40 overflow-auto scrollbar-thin whitespace-pre-wrap text-xs text-slate">{run.input}</pre>
                </div>
              </Card>
            )}

            {run.finalOutput && (
              <Card>
                <div className="border-b border-line px-5 py-4">
                  <h2 className="text-base font-semibold text-ink">Saída final</h2>
                </div>
                <div className="p-4">
                  <pre className="max-h-56 overflow-auto scrollbar-thin whitespace-pre-wrap text-xs text-ink">{run.finalOutput}</pre>
                </div>
              </Card>
            )}

            <Card>
              <div className="border-b border-line px-5 py-4">
                <h2 className="flex items-center gap-2 text-base font-semibold text-ink">
                  <Package className="h-4 w-4 text-navy" /> Artefatos
                </h2>
              </div>
              <div className="p-4">
                {run.artifacts.length === 0 ? (
                  <p className="py-4 text-center text-xs text-slate">Nenhum artefato gerado.</p>
                ) : (
                  <div className="space-y-2">
                    {run.artifacts.map((a) => (
                      <button
                        key={a.id}
                        onClick={() => setArtifact(a)}
                        className="flex w-full items-center justify-between rounded-lg border border-line px-3 py-2 text-left text-xs hover:bg-cloud"
                      >
                        <span className="font-medium text-ink">{a.name}</span>
                        <Badge tone="neutral">{a.type} v{a.version}</Badge>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      <Modal open={!!artifact} onClose={() => setArtifact(null)} title={artifact?.name} width="max-w-2xl">
        {artifact && <ArtifactViewer artifact={artifact} />}
      </Modal>
    </>
  );
}

function TimelineItem({ event, label }: { event: RunEvent; label?: string }) {
  const Icon =
    event.status === 'done'
      ? CheckCircle2
      : event.status === 'error'
        ? XCircle
        : event.status === 'awaiting_human'
          ? UserCheck
          : Loader2;
  const color =
    event.status === 'done'
      ? 'text-success'
      : event.status === 'error'
        ? 'text-danger'
        : event.status === 'awaiting_human'
          ? 'text-amber'
          : 'text-brand-orange';
  return (
    <li className="relative">
      <span className={`absolute -left-[31px] flex h-5 w-5 items-center justify-center rounded-full bg-white ${color}`}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-ink">
          {label || event.nodeId}
          {event.iteration ? <span className="ml-1 text-xs text-node-loop">iteração {event.iteration}</span> : null}
        </p>
        <span className="shrink-0 text-xs text-slate">{new Date(event.timestamp).toLocaleTimeString('pt-BR')}</span>
      </div>
      <p className="text-xs text-slate">{event.message}</p>
      {event.output && event.output.trim() && (
        <details className="mt-1.5 group">
          <summary className="cursor-pointer select-none text-[11px] font-medium text-brand-orange hover:underline">
            Ver saída deste nó
          </summary>
          <pre className="mt-1.5 max-h-48 overflow-auto scrollbar-thin whitespace-pre-wrap rounded-lg bg-cloud p-2.5 text-[11px] text-ink">
            {event.output}
          </pre>
        </details>
      )}
    </li>
  );
}
